/** Light (beige) or dark (#2c2c2c), chosen in the sidebar and remembered.
 *
 * The choice is written to <html data-theme>, which is what the tokens in
 * styles/tokens.css key off. First visit follows the system setting.
 */

import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "meeting-agent-theme";

function systemTheme(): Theme {
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function storedTheme(): Theme | null {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === "light" || saved === "dark" ? saved : null;
  } catch {
    return null; // private windows and blocked storage: fall back to the system
  }
}

export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(() => storedTheme() ?? systemTheme());

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* not being able to remember it is not worth an error */
    }
  }, [theme]);

  const toggle = useCallback(() => setTheme((now) => (now === "dark" ? "light" : "dark")), []);
  return [theme, toggle];
}
